import { BankAccountSchema, ControlUsageSchema, EntitlementsSchema, IsoDateSchema, PackageIdSchema, PASSWORD_MIN_LENGTH, SubscriptionPaymentSchema, SubscriptionStateSchema } from '@bak/types'
import { z } from 'zod'

// API contracts of the super console (shared by its Worker and its UI).

export const StaffRoleSchema = z.enum(['owner', 'support', 'billing'])
export type StaffRole = z.infer<typeof StaffRoleSchema>

const Password = z.string().min(PASSWORD_MIN_LENGTH, `At least ${PASSWORD_MIN_LENGTH} characters`).max(200)

export const LoginRequestSchema = z.object({ email: z.email(), password: z.string().min(1).max(200), turnstileToken: z.string().max(2048).optional() })
export const ChangePasswordRequestSchema = z.object({ currentPassword: z.string().min(1).max(200), newPassword: Password })

export const StaffSessionUserSchema = z.object({ id: z.string(), email: z.email(), name: z.string(), role: StaffRoleSchema })
export type StaffSessionUser = z.infer<typeof StaffSessionUserSchema>
export const MeResponseSchema = z.object({ user: StaffSessionUserSchema })

export const StaffDtoSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: StaffRoleSchema,
  active: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  /** Two-factor sign-in is on (#227). */
  twoFactor: z.boolean().default(false),
})
export type StaffDto = z.infer<typeof StaffDtoSchema>
export const StaffListSchema = z.object({ items: z.array(StaffDtoSchema) })

export const CreateStaffRequestSchema = z.object({
  email: z.email().max(200),
  name: z.string().trim().min(1).max(100),
  role: StaffRoleSchema,
  password: Password,
})
export const UpdateStaffRequestSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  role: StaffRoleSchema.optional(),
  active: z.boolean().optional(),
})

export const AuditEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  entity: z.string(),
  entityId: z.string().nullable(),
  detail: z.string().nullable(),
  staffName: z.string().nullable(),
  createdAt: z.string(),
})
export type AuditEntry = z.infer<typeof AuditEntrySchema>
export const AuditListSchema = z.object({ items: z.array(AuditEntrySchema) })

export const SiteStatusSchema = z.enum(['active', 'suspended'])
export type SiteStatus = z.infer<typeof SiteStatusSchema>

export const SiteSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  package: PackageIdSchema,
  status: SiteStatusSchema,
  webUrl: z.string(),
  adminUrl: z.string(),
  lastSyncAt: z.string().nullable(),
  lastSyncError: z.string().nullable(),
  usage: ControlUsageSchema.nullable(),
})
export type SiteSummary = z.infer<typeof SiteSummarySchema>
export const SiteListSchema = z.object({ items: z.array(SiteSummarySchema) })

export const ConsoleDashboardSchema = z.object({
  counts: z.object({ sites: z.number().int(), activeSites: z.number().int(), suspendedSites: z.number().int(), staff: z.number().int(), failedBuilds: z.number().int() }),
  recentActivity: z.array(AuditEntrySchema),
})
export type ConsoleDashboard = z.infer<typeof ConsoleDashboardSchema>

// ── Site registry ────────────────────────────────────────────────────────

const Origin = z
  .string()
  .trim()
  .max(200)
  .transform((v) => v.replace(/\/+$/, ''))
  .pipe(z.url({ protocol: /^https?$/ }))

export const SiteSlugSchema = z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/, 'Lowercase letters, numbers and dashes (3-40 characters)')

/** A site as the console shows and edits it (secrets never included). */
export const SiteDetailSchema = SiteSummarySchema.extend({
  notes: z.string(),
  /** The package, overrides and suspension the site last confirmed. */
  stored: EntitlementsSchema,
  hasControlSecret: z.boolean(),
  /** Whether Rebuild and CMS-triggered builds are switched on for this site. */
  buildEnabled: z.boolean(),
  createdAt: z.string(),
})
export type SiteDetail = z.infer<typeof SiteDetailSchema>

export const RegisterSiteRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
  slug: SiteSlugSchema,
  webUrl: Origin,
  adminUrl: Origin,
  /** The `CONTROL_SECRET` set on the site's API. Stored sealed and never shown again. */
  controlSecret: z.string().min(32).max(200),
  notes: z.string().max(2000).default(''),
})
export type RegisterSiteRequest = z.infer<typeof RegisterSiteRequestSchema>

export const UpdateSiteRequestSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  webUrl: Origin.optional(),
  adminUrl: Origin.optional(),
  notes: z.string().max(2000).optional(),
  /** Replace the stored control secret. */
  controlSecret: z.string().min(32).max(200).optional(),
})
export type UpdateSiteRequest = z.infer<typeof UpdateSiteRequestSchema>

/** Set a site's package and per-site exceptions; the console pushes it to the site and confirms it. */
export const SitePlanRequestSchema = EntitlementsSchema.omit({ suspended: true })
export type SitePlanRequest = z.infer<typeof SitePlanRequestSchema>

export const SiteSuspendRequestSchema = z.object({ suspended: z.boolean() })

export const SyncAllResultSchema = z.object({ synced: z.number().int(), failed: z.number().int() })

// ── Builds ───────────────────────────────────────────────────────────────

export const BuildStatusSchema = z.enum(['queued', 'running', 'succeeded', 'failed', 'skipped'])
export type BuildStatus = z.infer<typeof BuildStatusSchema>

/** Who asked for a build: staff (`manual`), a content change in the site's CMS (`cms`), a code change on main (`code`), or a run the console did not start (`pipeline`). */
export const BuildTriggerSchema = z.enum(['manual', 'cms', 'code', 'pipeline'])
export type BuildTrigger = z.infer<typeof BuildTriggerSchema>

export const SiteBuildSchema = z.object({
  id: z.string(),
  status: BuildStatusSchema,
  trigger: BuildTriggerSchema,
  requestedBy: z.string().nullable(),
  externalRunId: z.string().nullable(),
  message: z.string().nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
})
export type SiteBuild = z.infer<typeof SiteBuildSchema>
export const SiteBuildListSchema = z.object({ items: z.array(SiteBuildSchema) })

export const SiteBuildConfigRequestSchema = z.object({ enabled: z.boolean() })

/** What the build workflow reports back (signed with the console's build secret). */
export const BuildReportSchema = z.object({
  site: SiteSlugSchema,
  status: z.enum(['running', 'succeeded', 'failed']),
  runId: z.string().trim().min(1).max(40),
  /** The console's build id, when the console started the run (the workflow's `build_id` input). */
  buildId: z.string().trim().min(1).max(64).optional(),
  message: z.string().max(500).optional(),
})
export type BuildReport = z.infer<typeof BuildReportSchema>

/** A site asks for a build of its public web after a content change (signed with the site's own control secret). */
export const RebuildHookRequestSchema = z.object({ site: SiteSlugSchema })

/** What `POST /api/hooks/rebuild-all` started after a code change: one entry per site with builds switched on. */
export const RebuildAllResultSchema = z.object({
  started: z.array(z.string()),
  skipped: z.array(z.object({ site: z.string(), reason: z.string() })),
})
export type RebuildAllResult = z.infer<typeof RebuildAllResultSchema>

// ── Custom domains (issue #83) ───────────────────────────────────────────

export const SiteDomainStatusSchema = z.enum(['pending_ns', 'active', 'attached', 'error'])
export type SiteDomainStatus = z.infer<typeof SiteDomainStatusSchema>

export const DNS_RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT'] as const
export const DnsRecordTypeSchema = z.enum(DNS_RECORD_TYPES)
export type DnsRecordType = z.infer<typeof DnsRecordTypeSchema>

/** A DNS record of the client's zone, as the console shows it. */
export const DnsRecordSchema = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  content: z.string(),
  priority: z.number().int().nullable(),
  proxied: z.boolean().nullable(),
  /** Part of the client's email (MX, SPF, DKIM, DMARC, autodiscover), so it must be right before the switch. */
  email: z.enum(['mx', 'spf', 'dkim', 'dmarc', 'autodiscover']).nullable(),
})
export type DnsRecord = z.infer<typeof DnsRecordSchema>

export const SiteDomainSchema = z.object({
  id: z.string(),
  domain: z.string(),
  webHostnames: z.array(z.string()),
  adminHostname: z.string(),
  zoneId: z.string(),
  status: SiteDomainStatusSchema,
  /** Cloudflare's own zone status (`pending`, `active`, `moved`…), as last read; null when it could not be read. */
  zoneStatus: z.string().nullable(),
  nameServers: z.array(z.string()),
  lastCheckedAt: z.string().nullable(),
  error: z.string().nullable(),
  attachedAt: z.string().nullable(),
  /** The site's main domain: its address and CMS; the site's other domains redirect to it. */
  isPrimary: z.boolean(),
  /** The mailbox's domain (one per site, set apart from the primary). */
  isMail: z.boolean(),
  createdAt: z.string(),
})
export type SiteDomain = z.infer<typeof SiteDomainSchema>

/** One of the site's domains with its zone's records, as the domains list shows it. */
export const SiteDomainEntrySchema = z.object({
  domain: SiteDomainSchema,
  /** The zone's A, AAAA, CNAME, MX and TXT records (email ones flagged). */
  records: z.array(DnsRecordSchema),
  /**
   * Records the next step deletes, which staff confirm: connecting deletes A/AAAA/CNAME on the hostnames it attaches,
   * making a connected domain primary deletes them on its CMS hostname.
   */
  replacements: z.array(DnsRecordSchema),
  /** Why the records could not be read, if they could not. */
  recordsError: z.string().nullable(),
})
export type SiteDomainEntry = z.infer<typeof SiteDomainEntrySchema>

/** How many domains the site's package allows, and how many it has. */
export const SiteDomainLimitSchema = z.object({
  package: PackageIdSchema,
  /** The package's `domains` limit with the site's overrides; 0 when the package has no own domain. */
  limit: z.number().int(),
  /** Every domain the site has, whatever its status: a new one is refused once this reaches the limit. */
  used: z.number().int(),
  /** Connected domains. */
  connected: z.number().int(),
  /** Connected domains over the limit (after a downgrade). Shown as a warning; nothing is removed automatically. */
  over: z.number().int(),
  /** Why no domain can be added now, if none can. */
  blocked: z.enum(['not_in_package', 'at_limit']).nullable(),
})
export type SiteDomainLimit = z.infer<typeof SiteDomainLimitSchema>

export const SiteDomainsResponseSchema = z.object({
  /** False when the console has no Cloudflare token or account: domains cannot be added. */
  connected: z.boolean(),
  /** The Workers the hostnames are attached to. */
  workers: z.object({ web: z.string(), admin: z.string() }),
  limit: SiteDomainLimitSchema,
  /** The primary first, then in the order they were added. */
  domains: z.array(SiteDomainEntrySchema),
})
export type SiteDomainsResponse = z.infer<typeof SiteDomainsResponseSchema>

export const CreateSiteDomainRequestSchema = z.object({
  domain: z.string().trim().min(1).max(253),
  /** The CMS answers on `<adminSubdomain>.<domain>`. */
  adminSubdomain: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^(?!-)[a-z0-9-]{1,63}(?<!-)$/, 'Letters, numbers and dashes')
    .refine((v) => v !== 'www', 'www is the website')
    .default('cms'),
})
export type CreateSiteDomainRequest = z.infer<typeof CreateSiteDomainRequestSchema>

const RecordName = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(253)
  .regex(/^(@|(\*\.)?[a-z0-9_-]+(\.[a-z0-9_-]+)*\.?)$/, 'Use @ for the domain itself, or a name such as mail')

/** A record staff add to the client's zone (usually one missing from the imported ones). */
export const CreateDnsRecordRequestSchema = z
  .object({
    type: DnsRecordTypeSchema,
    /** `@`, a name relative to the domain (`mail`), or the full name. */
    name: RecordName,
    content: z.string().trim().min(1).max(2048),
    /** MX only. */
    priority: z.number().int().min(0).max(65535).optional(),
  })
  .superRefine((r, ctx) => {
    const bad = (message: string, path: string = 'content') => ctx.addIssue({ code: 'custom', path: [path], message })
    const host = /^(?!-)[a-z0-9_-]{1,63}(?<!-)(\.(?!-)[a-z0-9_-]{1,63}(?<!-))+\.?$/i
    if (r.type === 'A' && !z.ipv4().safeParse(r.content).success) bad('Enter an IPv4 address such as 192.0.2.1')
    if (r.type === 'AAAA' && !z.ipv6().safeParse(r.content).success) bad('Enter an IPv6 address')
    if ((r.type === 'CNAME' || r.type === 'MX') && (!host.test(r.content) || r.content.length > 253)) bad('Enter a hostname such as mail.example.com')
    if (r.type === 'MX' && r.priority === undefined) bad('MX records need a priority', 'priority')
    if (r.type === 'TXT' && r.content.length > 2048) bad('Too long')
  })
export type CreateDnsRecordRequest = z.infer<typeof CreateDnsRecordRequestSchema>

/**
 * Connect a domain, or make a connected one primary: `replace` is the ids of the records staff saw would be replaced
 * (must still match).
 */
export const AttachSiteDomainRequestSchema = z.object({ replace: z.array(z.string().max(64)).max(50) })

// ── Subscriptions and renewal payments ───────────────────────────────────

/** A renewal payment in the console's queue, with the site it came from. */
export const ConsolePaymentSchema = SubscriptionPaymentSchema.extend({
  site: z.object({ id: z.string(), slug: z.string(), name: z.string() }),
  package: z.string(),
  /** The staff member who approved or rejected it. */
  decidedBy: z.string().nullable(),
  /** The site has the decision (else it is resent on the next sync). */
  siteSynced: z.boolean(),
})
export type ConsolePayment = z.infer<typeof ConsolePaymentSchema>
export const ConsolePaymentListSchema = z.object({ items: z.array(ConsolePaymentSchema), pending: z.number().int() })

export const RejectPaymentRequestSchema = z.object({ reason: z.string().trim().min(3, 'Say why, in a few words').max(500) })

/** The outcome of an approval or rejection: the payment, and whether the site has heard of it yet. */
export const PaymentDecisionResultSchema = z.object({ payment: ConsolePaymentSchema, siteUpdated: z.boolean(), siteError: z.string().nullable() })
export type PaymentDecisionResult = z.infer<typeof PaymentDecisionResultSchema>

/** A site's subscription as the console shows and edits it. */
export const ConsoleSubscriptionSchema = z.object({
  siteId: z.string(),
  /** Nothing set up yet: the site runs as before (stage `none`). */
  configured: z.boolean(),
  exempt: z.boolean(),
  monthlyPrice: z.number().int(),
  startedAt: z.string().nullable(),
  paidUntil: z.string().nullable(),
  package: PackageIdSchema,
  state: SubscriptionStateSchema,
})
export type ConsoleSubscription = z.infer<typeof ConsoleSubscriptionSchema>

/** Staff set the subscription by hand when a client agrees: package, price per month, start and paid-until dates. */
export const UpdateSubscriptionRequestSchema = z
  .object({
    exempt: z.boolean(),
    package: PackageIdSchema,
    monthlyPrice: z.number().int().min(0).max(1_000_000_000),
    startedAt: IsoDateSchema.nullable(),
    paidUntil: IsoDateSchema.nullable(),
  })
  .refine((v) => v.exempt || v.monthlyPrice > 0, { path: ['monthlyPrice'], message: 'Enter the price per month' })
  .refine((v) => !v.startedAt || !v.paidUntil || v.startedAt <= v.paidUntil, { path: ['paidUntil'], message: 'Paid until must be on or after the start' })
export type UpdateSubscriptionRequest = z.infer<typeof UpdateSubscriptionRequestSchema>

export const SubscriptionUpdateResultSchema = z.object({ subscription: ConsoleSubscriptionSchema, siteUpdated: z.boolean(), siteError: z.string().nullable() })

/** Sites whose subscription needs attention: expiring within 14 days or expired, plus sites not set up yet. */
export const ExpiringListSchema = z.object({
  items: z.array(z.object({ site: z.object({ id: z.string(), slug: z.string(), name: z.string() }), subscription: ConsoleSubscriptionSchema })),
})

export const SubscriptionLogEntryDtoSchema = z.object({
  id: z.string(),
  action: z.enum(['approve', 'reject', 'manual']),
  staffName: z.string(),
  siteSlug: z.string(),
  months: z.number().int().nullable(),
  amount: z.number().int().nullable(),
  oldPaidUntil: z.string().nullable(),
  newPaidUntil: z.string().nullable(),
  proofKey: z.string().nullable(),
  proofSha256: z.string().nullable(),
  receiptNo: z.string().nullable(),
  detail: z.string(),
  createdAt: z.string(),
})
export type SubscriptionLogEntryDto = z.infer<typeof SubscriptionLogEntryDtoSchema>
export const SubscriptionLogListSchema = z.object({ items: z.array(SubscriptionLogEntryDtoSchema) })

/** Billing settings: the bank account clients transfer to. */
export const BillingSettingsSchema = z.object({ bank: BankAccountSchema.nullable() })
export const UpdateBillingSettingsRequestSchema = z.object({ bank: BankAccountSchema })

// ── Client requests (domain help, mail-domain change) ────────────────────

export const SiteRequestSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  site: z.object({ slug: z.string(), name: z.string() }).optional(),
  kind: z.enum(['domain_help', 'mail_domain_change']),
  status: z.enum(['open', 'done', 'rejected']),
  note: z.string(),
  domain: z.string().nullable(),
  requestedBy: z.string(),
  staffNote: z.string(),
  resolvedBy: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
})
export type SiteRequest = z.infer<typeof SiteRequestSchema>

export const SiteRequestListSchema = z.object({ items: z.array(SiteRequestSchema) })
export type SiteRequestList = z.infer<typeof SiteRequestListSchema>

export const ResolveSiteRequestSchema = z.object({ status: z.enum(['done', 'rejected']), staffNote: z.string().trim().max(1000).default('') })
export type ResolveSiteRequest = z.infer<typeof ResolveSiteRequestSchema>

// ── Mail domain move (staff) ─────────────────────────────────────────────

/** What moving the mail domain to another connected domain does: the addresses and mail that will be deleted. */
export const MailMovePreviewSchema = z.object({
  from: z.string().nullable(),
  to: z.string(),
  /** Addresses on the old mail domain, and how much mail each has. Empty when there is no old mail domain. */
  addresses: z.array(z.object({ address: z.string(), label: z.string(), users: z.number().int(), messages: z.number().int() })),
  /** Mail to unlisted addresses on the old domain (the catch-all). */
  otherMessages: z.number().int(),
})
export type MailMovePreview = z.infer<typeof MailMovePreviewSchema>

export const MailMoveRequestSchema = z.object({
  domainId: z.string().min(1),
  /** Staff confirmed the list of addresses that lose their mail. */
  confirm: z.literal(true),
  /** The client's request this carries out, closed as done. */
  requestId: z.string().optional(),
})
export type MailMoveRequest = z.infer<typeof MailMoveRequestSchema>

export const MailMoveResultSchema = z.object({ from: z.string().nullable(), to: z.string(), deletedAddresses: z.number().int(), deletedMessages: z.number().int() })
export type MailMoveResult = z.infer<typeof MailMoveResultSchema>
